/**
 * Search drawer behaviour that the theme bundle cannot provide.
 *
 * Loaded from sections/search-drawer.liquid. Everything that DialogElement already does — focus
 * trap, Escape, outside click, scroll lock, role="dialog"/aria-modal, inert, aria-expanded sync —
 * is deliberately NOT reimplemented here.
 */
(function () {
  'use strict';

  if (window.__hbSearch) return;
  window.__hbSearch = true;

  var DRAWER_ID = 'search-drawer';

  /* ------------------------------------------------------------------------------------------
   * Open from the right on every viewport.
   *
   * SearchDrawer.openFrom in assets/theme.js.liquid reads:
   *
   *   return matchMedia(breakpoints["sm-max"]).matches ? "top" : this.getAttribute("open-from") || "top";
   *
   * The desktop branch already honours the attribute, so the section sets open-from="right" and
   * desktop needs nothing here. Mobile hard-returns "top", so the accessor is replaced on the
   * prototype. Class accessors are configurable, which makes this legal and stable.
   *
   * Patching assets/theme.js.liquid instead would be worse: it is minified bundle output with no
   * build pipeline, so any Impact update silently drops edits made there.
   *
   * The original getter also force-shows `.custom-judgeme` elements as a side effect. That is an
   * old hack living in the wrong place, but removing it is out of scope here — it is carried over
   * unchanged so behaviour does not shift beneath anyone.
   */
  function overrideOpenFrom() {
    var Ctor = window.customElements.get(DRAWER_ID);
    if (!Ctor) return;

    try {
      Object.defineProperty(Ctor.prototype, 'openFrom', {
        configurable: true,
        get: function () {
          document.querySelectorAll('.custom-judgeme').forEach(function (el) {
            el.style.display = 'block';
          });
          /* Opened from inside the mobile menu (assets/heybalu-mobile-nav.js): unfold downwards
             below the menu's top bar instead of sliding in from the side. */
          /* Closed together with the menu (its close button): leave to the left like the menu does. */
          if (document.documentElement.classList.contains('hb-search-close-left')) return 'left';
          if (document.documentElement.classList.contains('hb-search-from-nav')) return 'top';
          return this.getAttribute('open-from') || 'right';
        }
      });
    } catch (e) {
      /* Non-fatal: the drawer keeps opening from the top, which is the previous behaviour. */
    }
  }

  /* ------------------------------------------------------------------------------------------
   * Focus the search field on open.
   *
   * DialogElement passes `initialFocus: matchMedia("(pointer: fine)").matches ? … : false`, so on
   * touch devices it deliberately focuses nothing to avoid the keyboard popping up unasked. The
   * brief asks for the field to be ready to type in, so focus is applied after the open animation
   * on those devices.
   *
   * Note for testing on real iOS: Safari only raises the keyboard for focus() inside the original
   * user-gesture task. dialog:after-show fires after the ~0.55s Motion One timeline, so the field
   * may receive focus without the keyboard appearing. If that turns out to matter, focus has to be
   * called synchronously from the trigger's click handler instead.
   */
  function focusFieldOnOpen(drawer) {
    drawer.addEventListener('dialog:after-show', function () {
      var input = drawer.querySelector('input[type="search"], input[name="q"]');
      if (!input) return;

      /* See quietFocus(): the ring belongs to keyboard navigation, not to a field that was focused
         for you or clicked into. */
      input.setAttribute('data-hb-quiet-focus', '');
      try {
        input.focus({ preventScroll: true });
      } catch (e) {
        input.focus();
      }
    });

  }

  /* ------------------------------------------------------------------------------------------
   * The focus ring belongs to the keyboard.
   *
   * theme.css draws a 3px outline on `input:focus-visible` (WCAG 2.4.7). Browsers match
   * :focus-visible on text inputs even for a plain mouse click, so clicking the field produced the
   * same heavy ring as tabbing to it. That rule stays — it is what makes keyboard navigation
   * visible — but pointer-initiated focus is marked and skipped, and the mark is cleared on blur,
   * so the very next focus by keyboard shows the ring again.
   */
  function quietFocus(input, scope) {
    if (!input || input.__hbQuiet) return;
    input.__hbQuiet = true;

    /* Any pointer interaction in this area, not just on the field itself. Reset, chips and the
       correction suggestion all hand focus back to the input, and each one would otherwise need its
       own exception — the reset button was exactly that case. Pointer means "not tabbing", which is
       the whole distinction being drawn here. */
    (scope || input).addEventListener('pointerdown', function () {
      input.setAttribute('data-hb-quiet-focus', '');
    });
    input.addEventListener('blur', function () {
      input.removeAttribute('data-hb-quiet-focus');
    });
    /* Tab into the field: no mark, so the outline appears as the accessibility rule intends. */
    input.addEventListener('keydown', function (event) {
      if (event.key === 'Tab') input.removeAttribute('data-hb-quiet-focus');
    });
  }

  /* ------------------------------------------------------------------------------------------
   * Frequent-search chips.
   *
   * Each chip is a real link to the search page, so it works without JS and can be opened in a new
   * tab. When JS is available the click is intercepted: the term goes into the field and the normal
   * search runs, which keeps the customer inside the drawer instead of navigating away.
   */
  function wireChips(drawer) {
    drawer.addEventListener('click', function (event) {
      var chip = event.target.closest('[data-hb-chip]');
      if (!chip) return;
      if (event.metaKey || event.ctrlKey || event.shiftKey || event.button !== 0) return;

      var input = drawer.querySelector('input[type="search"], input[name="q"]');
      if (!input) return;

      event.preventDefault();
      input.value = chip.getAttribute('data-hb-chip') || '';
      /* Focus we grant, not focus the customer asked for by tabbing — so no outline. */
      input.setAttribute('data-hb-quiet-focus', '');
      input.focus();
      /* The theme's search listens for `input`, so dispatching it starts the same debounce as typing. */
      input.dispatchEvent(new Event('input', { bubbles: true }));
    });
  }

  /* ------------------------------------------------------------------------------------------
   * Direct to collection.
   *
   * Searching for a whole product type ("Poster", "Fussmatte") should land in that collection, not
   * on a generic results page. The mapping is not a separate list: it is read off the rendered
   * no-results tiles, each of which carries its terms and its collection URL. One place to maintain,
   * no hardcoded handles, and it localises itself because the URL comes from Shopify.
   *
   * Deliberately client-side. The search app's own "search redirects" would also fire on the live
   * preview request, so the drawer could never show results for these terms at all.
   */
  function normalise(value) {
    return String(value || '')
      .toLowerCase()
      .trim()
      .replace(/\u00df/g, 'ss')                        /* ß -> ss, so "Fußmatte" matches "Fussmatte" */
      .normalize('NFD').replace(/[\u0300-\u036f]/g, '') /* strip accents, for FR */
      .replace(/\s+/g, ' ');
  }

  function collectionFor(drawer, term) {
    var key = normalise(term);
    if (!key) return null;

    var tiles = drawer.querySelectorAll('[data-hb-terms][data-hb-url]');
    for (var i = 0; i < tiles.length; i++) {
      var terms = (tiles[i].getAttribute('data-hb-terms') || '').split(',');
      for (var j = 0; j < terms.length; j++) {
        if (normalise(terms[j]) && normalise(terms[j]) === key) {
          return tiles[i].getAttribute('data-hb-url');
        }
      }
    }
    return null;
  }

  function wireDirectToCollection(scope) {
    var form = scope.querySelector('form[role="search"], form[action*="search"]');
    if (!form || form.__hbRouted) return;
    form.__hbRouted = true;

    form.addEventListener('submit', function (event) {
      var input = form.querySelector('input[type="search"], input[name="q"]');
      if (!input) return;
      var url = collectionFor(document, input.value);
      if (!url) return;   /* nothing matched -> the normal search submit stands */
      event.preventDefault();
      window.location.assign(url);
    });
  }

  /* ------------------------------------------------------------------------------------------
   * "Did you mean…?"
   *
   * The suggestion appears whenever a misspelling is recognised — including when the search still
   * returned something. A typo that happens to match a product is still a typo, and the better term
   * is worth offering.
   *
   * Two steps: the curated per-market typo list from snippets/search-config.liquid first, then a
   * Levenshtein match against the vocabulary already on the page (tile terms and chips), so unlisted
   * typos are caught too without maintaining a second list.
   *
   * What is reserved for an EMPTY result is the automatic re-search: only then does the corrected
   * query run by itself, so the drawer is never a dead end, and the note carries a way back to the
   * original spelling. With results on screen the suggestion stays passive until it is pressed.
   */
  /* ------------------------------------------------------------------------------------------
   * Moving rendered results into place.
   *
   * Nodes are imported and swapped, never assigned as an HTML string. The markup comes from our own
   * storefront and Shopify escapes the query it echoes back, so nothing here was exploitable — but
   * `innerHTML =` cannot show that at a glance, and a scanner reading this file has to assume the
   * worst. importNode also matches what the theme itself does for the same job
   * (assets/theme.js.liquid, PredictiveSearch).
   */
  function swapChildren(target, source) {
    if (!target) return;
    if (!source) { target.replaceChildren(); return; }
    /* Copy the NodeList into an array first: it is live, and moving the nodes would mutate the very
       list being iterated. */
    var nodes = Array.prototype.slice.call(document.importNode(source, true).childNodes);
    target.replaceChildren.apply(target, nodes);
  }

  /* Every request goes to this storefront. Building the URL against the current origin and refusing
     anything else keeps that guaranteed rather than merely intended — Shopify.routes.root is a
     relative path, but it is read from the page and worth pinning down. */
  function storefrontUrl(path) {
    try {
      var url = new URL(path, window.location.origin);
      return url.origin === window.location.origin ? url.toString() : null;
    } catch (e) {
      return null;
    }
  }

  function config() {
    if (config._cache !== undefined) return config._cache;
    var el = document.getElementById('hb-search-config');
    try { config._cache = el ? JSON.parse(el.textContent) : null; }
    catch (e) { config._cache = null; }
    return config._cache;
  }

  /* Standard iterative Levenshtein. Small vocabulary (tens of entries), so the simple version is
     plenty and avoids pulling in a dependency for the theme's first fuzzy match. */
  function distance(a, b) {
    if (a === b) return 0;
    if (!a.length) return b.length;
    if (!b.length) return a.length;

    var prev = new Array(b.length + 1);
    for (var j = 0; j <= b.length; j++) prev[j] = j;

    for (var i = 1; i <= a.length; i++) {
      var curr = [i];
      for (var k = 1; k <= b.length; k++) {
        var cost = a.charAt(i - 1) === b.charAt(k - 1) ? 0 : 1;
        curr[k] = Math.min(curr[k - 1] + 1, prev[k] + 1, prev[k - 1] + cost);
      }
      prev = curr;
    }
    return prev[b.length];
  }

  /* Every term the shop already knows about: the tiles' routing terms plus the chips. */
  function vocabulary() {
    var words = [];
    document.querySelectorAll('[data-hb-terms]').forEach(function (el) {
      (el.getAttribute('data-hb-terms') || '').split(',').forEach(function (t) {
        var v = t.trim();
        if (v) words.push(v);
      });
    });
    document.querySelectorAll('[data-hb-chip]').forEach(function (el) {
      var v = (el.getAttribute('data-hb-chip') || '').trim();
      if (v) words.push(v);
    });
    var cfg = config();
    if (cfg && cfg.typos) {
      Object.keys(cfg.typos).forEach(function (k) { words.push(cfg.typos[k]); });
    }
    return words;
  }

  function correctionFor(term) {
    var cfg = config();
    var key = normalise(term);
    if (!key || key.length < 3) return null;

    if (cfg && cfg.typos) {
      var listed = cfg.typos[key];
      if (listed && normalise(listed) !== key) return listed;
    }

    var lev = (cfg && cfg.levenshtein) || { shortMaxDistance: 1, longMaxDistance: 2, shortUpTo: 4 };
    var allowed = key.length <= lev.shortUpTo ? lev.shortMaxDistance : lev.longMaxDistance;

    var best = null, bestDistance = Infinity;
    vocabulary().forEach(function (word) {
      var candidate = normalise(word);
      if (!candidate || candidate === key) return;
      var d = distance(key, candidate);
      if (d < bestDistance && d <= allowed) { bestDistance = d; best = word; }
    });
    return best;
  }

  function suggestUrl(term) {
    var host = document.querySelector('predictive-search[section-id]');
    if (!host) return null;
    var root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    return storefrontUrl(root + 'search/suggest?q=' + encodeURIComponent(term) +
           '&section_id=' + encodeURIComponent(host.getAttribute('section-id')) +
           '&resources%5Blimit%5D=10&resources%5Blimit_scope%5D=each');
  }

  /* Builds the clickable suggestion and puts it at the top of `container`. Shared by the drawer and
     the results page so both behave the same. Returns the note, or null when nothing was corrected. */
  function offerCorrection(container, input, term) {
    if (!container || !input || !term) return null;
    if (container.querySelector('[data-hb-correction]')) return null;

    var corrected = correctionFor(term);
    if (!corrected || normalise(corrected) === normalise(term)) return null;

    var cfg = config();
    var strings = (cfg && cfg.strings) || {};

    var note = document.createElement('div');
    note.className = 'hb-search__correction';
    note.setAttribute('data-hb-correction', '');
    note.setAttribute('role', 'status');

    /* The suggestion is the control: pressing it puts the corrected term in the field and runs the
       normal search, so what is on screen always matches what the field says. */
    var suggest = document.createElement('button');
    suggest.type = 'button';
    suggest.className = 'hb-search__correction-suggest';
    suggest.appendChild(withTerm('span', strings.didYouMean || 'Did you mean {{ term }}?', corrected, 'b'));
    suggest.addEventListener('click', function () {
      input.value = corrected;
      input.setAttribute('data-hb-quiet-focus', '');
      input.focus();
      input.dispatchEvent(new Event('input', { bubbles: true }));
    });
    note.appendChild(suggest);

    container.insertBefore(note, container.firstChild);
    note.__hbCorrected = corrected;
    return note;
  }

  function wireDidYouMean(drawer) {
    var lastTerm = null;

    function onResults() {
      var slot = drawer.querySelector('[slot="results"]');
      if (!slot) return;

      var input = drawer.querySelector('input[type="search"], input[name="q"]');
      var term = input ? input.value.trim() : '';
      if (!term) { lastTerm = null; return; }

      var hadResults = !!slot.querySelector('product-card');
      var note = offerCorrection(slot, input, term);
      if (!note) return;

      var corrected = note.__hbCorrected;
      var cfg = config();
      var strings = (cfg && cfg.strings) || {};

      /* Nothing found at all: run the correction straight away so the drawer is not a dead end, and
         offer the way back to the original spelling. */
      if (!hadResults && term !== lastTerm) {
        lastTerm = term;
        var url = suggestUrl(corrected);
        if (!url) return;

        fetch(url).then(function (r) { return r.text(); }).then(function (html) {
          var doc = new DOMParser().parseFromString(html, 'text/html');
          var fresh = doc.querySelector('[slot="results"]');
          var target = drawer.querySelector('[slot="results"]');
          if (!fresh || !target || !fresh.querySelector('product-card')) return;

          swapChildren(target, fresh);
          target.insertBefore(note, target.firstChild);

          var undo = document.createElement('button');
          undo.type = 'button';
          undo.className = 'hb-search__correction-undo';
          undo.textContent = (strings.searchInstead || 'Search for “{{ term }}” instead')
            .replace('{{ term }}', term);
          undo.addEventListener('click', function () {
            undo.remove();
            var again = suggestUrl(term);
            if (!again) return;
            fetch(again).then(function (r) { return r.text(); }).then(function (h) {
              var d2 = new DOMParser().parseFromString(h, 'text/html');
              var f2 = d2.querySelector('[slot="results"]');
              var t2 = drawer.querySelector('[slot="results"]');
              if (f2 && t2) swapChildren(t2, f2);
            });
          });
          note.appendChild(undo);
        });
      }
    }

    new MutationObserver(onResults).observe(drawer, { childList: true, subtree: true });
  }

  /* "Meintest du {{ term }}?" -> <p>Meintest du <b>Hoodie</b>?</p>, built from text nodes. */
  function withTerm(tag, template, term, emphasisTag) {
    var el = document.createElement(tag);
    var parts = String(template).split('{{ term }}');
    el.appendChild(document.createTextNode(parts[0] || ''));
    var em = document.createElement(emphasisTag || 'b');
    em.textContent = term;
    el.appendChild(em);
    el.appendChild(document.createTextNode(parts[1] || ''));
    return el;
  }

  /* ------------------------------------------------------------------------------------------
   * Live search on the results page.
   *
   * Runs in place, under the page's own search field — the drawer is not pulled over the results
   * the customer is looking at. It uses the same predictive endpoint and the same rendered section
   * as the drawer, so both show identical results and there is no second rendering path to drift
   * apart. (The drawer's four-result cap went unnoticed for exactly that reason.)
   */
  function wirePageLiveSearch(drawer) {
    var input = document.querySelector('.shopify-section--main-search input[name="q"]');
    var panel = document.getElementById('hb-page-search-results');
    if (!input || !panel || input.__hbLive) return;
    input.__hbLive = true;

    quietFocus(input, input.closest('form') || input);

    var cfg = config();
    var minChars = (cfg && cfg.minChars) || 2;
    var timer = null;
    var controller = null;
    var cache = {};

    function clear() {
      panel.hidden = true;
      panel.replaceChildren();
    }

    function run(term) {
      var url = suggestUrl(term);
      if (!url) return;

      if (cache[term]) { render(cache[term], term); return; }
      if (controller) controller.abort();
      controller = new AbortController();

      fetch(url, { signal: controller.signal })
        .then(function (r) { return r.text(); })
        .then(function (html) { cache[term] = html; render(html, term); })
        .catch(function () { /* aborted by the next keystroke */ });
    }

    function render(html, term) {
      if (input.value.trim() !== term) return;   /* a later keystroke already won */
      var doc = new DOMParser().parseFromString(html, 'text/html');
      var fresh = doc.querySelector('[slot="results"]');
      if (!fresh) return clear();

      swapChildren(panel, fresh);
      panel.hidden = false;

      /* Same correction layer as the drawer, pointed at this panel. */
      offerCorrection(panel, input, term);
    }

    input.addEventListener('input', function () {
      var term = input.value.trim();
      clearTimeout(timer);
      if (term.length < minChars) { clear(); return; }
      timer = setTimeout(function () { run(term); }, 280);
    });
  }

  function init() {
    overrideOpenFrom();
    var drawer = document.getElementById(DRAWER_ID);
    if (!drawer) return;
    focusFieldOnOpen(drawer);
    wireChips(drawer);
    wireDirectToCollection(drawer);
    wireDidYouMean(drawer);

    var drawerInput = drawer.querySelector('input[type="search"], input[name="q"]');
    if (drawerInput) quietFocus(drawerInput, drawer);

    /* Results page: same routing on submit, plus live results in place. */
    var pageForm = document.querySelector('.shopify-section--main-search form[action*="search"]');
    if (pageForm) wireDirectToCollection(pageForm.parentNode || document);
    wirePageLiveSearch(drawer);
  }

  if (window.customElements && window.customElements.whenDefined) {
    window.customElements.whenDefined(DRAWER_ID).then(init);
  } else {
    init();
  }
})();
